import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type LifecycleFunnel, type LifecycleFunnelStep, type FirstRunSplit, type HabitSolve } from '../../api/adminApi';
import { listSegments, describeCriteria, type SegmentCriteria } from '../../api/segmentsApi';

/** Human labels for the day-1 funnel steps (order comes from the backend). */
const EVENT_LABELS: Record<string, string> = {
  first_run_started: 'First-run started',
  first_question_shown: 'Question shown',
  first_submission: 'Submitted',
  aha_feedback_seen: 'Saw feedback (aha)',
  next_question_shown: 'Next planted',
  guest_promoted: 'Signed up (guest→account)',
  day2_return: 'Returned (day 2+)',
};

/** "Within N days of arriving" — counted per person from their own arrival,
 *  not from the cohort's Monday. Empty is all time. */
const WINDOW_OPTIONS = [1, 3, 7, 14, 30];

/** Experiment keys whose variants are worth slicing the day-1 funnel by. */
const EXPERIMENT_KEYS = ['day1-modality', 'day1-subject', 'day1-difficulty', 'day1-auth-gate', 'guest-subscription'];

function StepTable({ steps }: { steps: LifecycleFunnelStep[] }) {
  const start = steps[0]?.users ?? 0;
  return (
    <table className="data-table" style={{ width: '100%' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left' }}>Step</th>
          <th style={{ textAlign: 'right' }}>Users</th>
          <th style={{ textAlign: 'right' }}>% of start</th>
          <th style={{ textAlign: 'right' }}>% of prev</th>
          <th style={{ width: '30%' }}>Conversion</th>
        </tr>
      </thead>
      <tbody>
        {steps.map((s) => (
          <tr key={s.event}>
            <td>{EVENT_LABELS[s.event] ?? s.event}</td>
            <td style={{ textAlign: 'right' }}>{s.users}</td>
            <td style={{ textAlign: 'right' }}>{s.pctOfStart}%</td>
            <td style={{ textAlign: 'right', color: s.pctOfPrev < 100 ? '#b45309' : undefined }}>{s.pctOfPrev}%</td>
            <td>
              <div style={{ background: '#eef2ff', borderRadius: 4, height: 16, overflow: 'hidden' }}>
                <div style={{
                  width: `${start ? (s.users / start) * 100 : 0}%`,
                  background: '#6366f1', height: '100%',
                }} />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Pick-screen branch off "First-run started": proceeded to a question vs. skipped. */
function FirstRunSplitBox({ split }: { split: FirstRunSplit }) {
  if (!split.started) return null;
  // Users who reached the pick screen but neither proceeded nor skipped (closed the tab).
  const dropped = Math.max(0, split.started - split.proceeded - split.skipped);
  const droppedPct = Math.round((dropped / split.started) * 1000) / 10;
  const Row = ({ label, users, pct, color }: { label: string; users: number; pct: number; color: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.25rem 0' }}>
      <span style={{ width: 190 }}>{label}</span>
      <span style={{ width: 40, textAlign: 'right' }}>{users}</span>
      <span style={{ width: 56, textAlign: 'right', color: '#6b7280' }}>{pct}%</span>
      <div style={{ flex: 1, background: '#eef2ff', borderRadius: 4, height: 14, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, background: color, height: '100%' }} />
      </div>
    </div>
  );
  return (
    <div style={{ margin: '0.5rem 0 1.5rem', padding: '0.75rem 1rem', background: '#f9fafb', borderRadius: 8, maxWidth: 640 }}>
      <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
        After First-run started ({split.started}) — pick screen
      </div>
      <Row label="↳ Proceeded to question" users={split.proceeded} pct={split.proceededPct} color="#6366f1" />
      <Row label="↳ Skipped for now" users={split.skipped} pct={split.skippedPct} color="#f59e0b" />
      {dropped > 0 && <Row label="↳ Left without choosing" users={dropped} pct={droppedPct} color="#9ca3af" />}
    </div>
  );
}

/** Surface labels for the habit "solved after completion" breakdown. */
const SOURCE_LABELS: Record<string, string> = {
  'daily-session': 'Daily session',
  'practice-hub': 'Practice hub',
  'subject-drill': 'Subject drill',
  review: 'Review',
  'battle-missed': 'Duel (practice missed)',
  placement: 'Placement',
  duel: 'Duel',
  test: 'Test / assignment',
  practice: 'Practice (other)',
  'first-run': 'First run',
  '(unknown)': 'Unknown',
};

/** Habit milestone: solved a question after finishing first-run, by surface. */
function HabitSolveBox({ habit }: { habit: HabitSolve }) {
  const solvedMax = habit.bySource.reduce((m, s) => Math.max(m, s.users), 0) || 1;
  return (
    <div style={{ margin: '0.5rem 0 1.5rem', padding: '0.75rem 1rem', background: '#f9fafb', borderRadius: 8, maxWidth: 640 }}>
      <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
        Solved a question after completion — {habit.solved} ({habit.pctOfStart}% of start)
      </div>
      {habit.solved === 0
        ? <p style={{ color: '#6b7280', margin: 0 }}>No post-first-run solves recorded yet.</p>
        : habit.bySource.map((s) => (
            <div key={s.source} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.25rem 0' }}>
              <span style={{ width: 150 }}>↳ {SOURCE_LABELS[s.source] ?? s.source}</span>
              <span style={{ width: 40, textAlign: 'right' }}>{s.users}</span>
              <span style={{ width: 56, textAlign: 'right', color: '#6b7280' }}>
                {Math.round((s.users / habit.solved) * 1000) / 10}%
              </span>
              <div style={{ flex: 1, background: '#eef2ff', borderRadius: 4, height: 14, overflow: 'hidden' }}>
                <div style={{ width: `${(s.users / solvedMax) * 100}%`, background: '#10b981', height: '100%' }} />
              </div>
            </div>
          ))}
    </div>
  );
}

/** Who the page is about, stated above every table it heads. */
function SegmentBanner({ segment }: { segment: NonNullable<LifecycleFunnel['segment']> }) {
  return (
    <div style={{
      margin: '0 0 1rem', padding: '0.75rem 1rem', borderRadius: 8, maxWidth: 720,
      border: '1px solid var(--border-color)', background: 'var(--surface)',
    }}>
      <div style={{ fontWeight: 600 }}>
        Segment: {segment.name} — {segment.population.toLocaleString()} people at the first step
      </div>
      <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', margin: '0.25rem 0', color: 'var(--text-secondary)' }}>
        {describeCriteria(segment.criteria as SegmentCriteria)}
      </div>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        Every table below counts only these people. Evaluated {new Date(segment.evaluatedAt).toLocaleString()}.
      </div>
      {/* A behavioural segment is a different set of people each day, so its
          figures cannot be compared with yesterday's as a change in behaviour. */}
      {!segment.stable && (
        <div style={{ fontSize: '0.85rem', color: 'var(--status-warning-text)', marginTop: '0.25rem' }}>
          Moving population: this segment is defined by recent behaviour, so who is in it changes day to day.
          A different number tomorrow may be different people, not different conversion.
        </div>
      )}
      {segment.truncated && (
        <div style={{ fontSize: '0.85rem', color: 'var(--status-error-text)', marginTop: '0.25rem' }}>
          Truncated: more people matched than one query can carry, so these counts are a floor.
        </div>
      )}
    </div>
  );
}

export function LifecycleFunnelTab() {
  const [cohortWeek, setCohortWeek] = useState<string>('');
  const [experimentKey, setExperimentKey] = useState<string>('');
  const [sliceBy, setSliceBy] = useState<string>('');
  const [segmentId, setSegmentId] = useState<string>('');
  const [windowDays, setWindowDays] = useState<number | ''>('');
  // `?link=` — opened from a published link's page: the funnel of the people
  // that link brought. A chosen segment takes over; Clear drops the link.
  const [params, setParams] = useSearchParams();
  const link = params.get('link') ?? '';
  const clearLink = () => { const next = new URLSearchParams(params); next.delete('link'); setParams(next); };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['lifecycle-funnel', cohortWeek, experimentKey, sliceBy, segmentId, windowDays, link],
    queryFn: () => adminApi.getLifecycleFunnel({
      cohortWeek: cohortWeek || undefined,
      experimentKey: experimentKey || undefined,
      sliceBy: sliceBy || undefined,
      segmentId: segmentId || undefined,
      windowDays: windowDays || undefined,
      link: !segmentId && link ? link : undefined,
    }),
  });
  // Same key as the Segments panel, so a segment defined there is offered here
  // without a reload.
  const segments = useQuery({ queryKey: ['segments'], queryFn: listSegments });

  const weeks = data?.availableWeeks ?? [];

  return (
    <div style={{ padding: '1rem 0' }}>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <label>
          Cohort week:{' '}
          <select value={cohortWeek || data?.cohortWeek || ''} onChange={(e) => setCohortWeek(e.target.value)}>
            {/* Resolved week first so the picker reflects the default even before a manual pick. */}
            {(weeks.length ? weeks : [data?.cohortWeek].filter(Boolean) as string[]).map((w) => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
        </label>
        <label>
          Segment:{' '}
          <select value={segmentId} onChange={(e) => setSegmentId(e.target.value)}>
            <option value="">(everyone)</option>
            {(segments.data?.segments ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label title="Count a step only if the person reached it within N days of their own arrival">
          Reached within:{' '}
          <select value={windowDays} onChange={(e) => setWindowDays(e.target.value ? Number(e.target.value) : '')}>
            <option value="">(all time)</option>
            {WINDOW_OPTIONS.map((d) => <option key={d} value={d}>{d} day{d === 1 ? '' : 's'} of arriving</option>)}
          </select>
        </label>
        <label>
          Slice by experiment:{' '}
          <select value={experimentKey} onChange={(e) => setExperimentKey(e.target.value)}>
            <option value="">(overall)</option>
            {EXPERIMENT_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label>
          Slice by acquisition:{' '}
          <select value={sliceBy} onChange={(e) => setSliceBy(e.target.value)}>
            <option value="">(overall)</option>
            {(data?.sliceOptions ?? []).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
      </div>

      {isLoading && <p>Loading funnel…</p>}
      {isError && <p style={{ color: '#b91c1c' }}>Failed to load lifecycle funnel.</p>}

      {data && (
        <>
          {data.segment && <SegmentBanner segment={data.segment} />}
          {data.link && (
            <div className="lf-link-banner">
              Published link <code>{data.link}</code> — people whose first touch was this link.
              <button type="button" className="lf-link-clear" onClick={clearLink}>Clear</button>
            </div>
          )}
          <h3>{data.segment ? data.segment.name : 'Overall'} — {data.cohortWeek}</h3>
          {/* A window can only be measured against a recorded arrival, and
              copied history has none — so a windowed figure can sit below the
              all-time one for reasons that are not behaviour. */}
          {data.windowDays && (
            <p style={{ color: 'var(--text-secondary)', margin: '0 0 0.75rem' }}>
              Counting only steps reached within {data.windowDays} day{data.windowDays === 1 ? '' : 's'} of each
              person’s arrival. People with no recorded arrival time are left out rather than guessed at.
            </p>
          )}
          {data.steps[0]?.users === 0
            ? <p style={{ color: '#6b7280' }}>No events recorded for this cohort week yet.</p>
            : <>
                <StepTable steps={data.steps} />
                {data.firstRunSplit && <FirstRunSplitBox split={data.firstRunSplit} />}
                {data.habitSolve && <HabitSolveBox habit={data.habitSolve} />}
              </>}

          {data.bySlice && data.bySlice.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h3>By {data.sliceBy}</h3>
              {/* Only milestones written since the tier-2 dual-write carry a
                  touch; copied history has none and groups under "(not
                  recorded)". Saying so here is cheaper than someone reading it
                  as "this campaign brought nobody". */}
              <p style={{ color: '#6b7280', margin: '0 0 0.75rem' }}>
                Rows recorded before acquisition capture group under “(not recorded)” —
                a historical step cannot be given an origin it never had.
              </p>
              {data.bySlice.map((v) => (
                <div key={v.slice} style={{ marginBottom: '1.25rem' }}>
                  <h4 style={{ margin: '0.5rem 0' }}>{v.slice}</h4>
                  <StepTable steps={v.steps} />
                </div>
              ))}
            </div>
          )}

          {data.byVariant && data.byVariant.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h3>By {data.experimentKey}</h3>
              {data.byVariant.map((v) => (
                <div key={v.variant} style={{ marginBottom: '1.25rem' }}>
                  <h4 style={{ margin: '0.5rem 0' }}>{v.variant}</h4>
                  <StepTable steps={v.steps} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
